import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Divider, Alert, Space, Switch, Typography } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

const UserModalEdit = ({ visible, user, loading, onSubmit, onClose }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || 'user',
        is_active: user.is_active ?? true,
        is_verified: user.is_verified ?? false,
        phone_number: user.phone_number || '',
        website_url: user.website_url || '',
      });
    }
  }, [user, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  return (
    <Modal
      title="Edit User"
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okText="Save Changes"
      cancelText="Cancel"
      width={600}
    >
      {user && (
        <Form form={form} layout="vertical">
          <Form.Item
            label="Full Name"
            name="full_name"
            rules={[
              { required: true, message: 'Please enter full name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 100, message: 'Name must not exceed 100 characters' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter email" disabled />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone_number"
            rules={[
              {
                pattern: /^[+]?[0-9\s\-\(\)]{10,15}$/,
                message: 'Please enter a valid phone number',
              },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            label="Website URL"
            name="website_url"
            rules={[
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input prefix={<GlobalOutlined />} placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              <Option value="user">User</Option>
              <Option value="admin">Admin</Option>
              <Option value="super_admin">Super Admin</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item label="Status Settings">
            <Space direction="vertical">
              <Form.Item name="is_active" valuePropName="checked" noStyle>
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
              <Text type="secondary">Set user account as active or inactive</Text>

              <Form.Item name="is_verified" valuePropName="checked" noStyle>
                <Switch checkedChildren="Verified" unCheckedChildren="Unverified" />
              </Form.Item>
              <Text type="secondary">Verify user email address</Text>
            </Space>
          </Form.Item>

          <Alert
            message="Note"
            description="Changing user role or status may affect their permissions and access."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        </Form>
      )}
    </Modal>
  );
};

export default UserModalEdit;
